<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-semibold">Video Library</h2>
      <Button variant="outline" size="sm" :disabled="isSharingSet" @click="handleShareSet">
        <Icon v-if="!isSharingSet" name="heroicons:share" class="h-4 w-4 mr-1.5" />
        <Icon v-else name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
        {{ isSharingSet ? 'Creating Link...' : 'Share Video Set' }}
      </Button>
    </div>

    <div class="mb-4">
      <FilterInput v-model="searchQuery" placeholder="Search videos..." />
    </div>

    <div v-if="YouTubeBubbles.length === 0" class="text-center py-8">
      <p class="text-gray-600 mb-4">You don't have any videos yet.</p>
      <Button @click="navigateToAddVideo">Add Your First Video</Button>
    </div>
    <div v-else>
      <div class="mb-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold text-center w-full">Featured Video</h3>
          <Button variant="ghost" size="sm" @click="getNewFeaturedVideo">
            <Icon name="heroicons:arrow-path" class="w-4 h-4" />
          </Button>
        </div>
        <AppLink v-if="featuredVideo" :to="`/bubble/${featuredVideo.uuid}`">
          <Card
            class="max-w-4xl mx-auto cursor-pointer hover:shadow-lg transition-shadow duration-300"
          >
            <div class="grid grid-cols-1 md:grid-cols-2">
              <div class="w-full">
                <AspectRatio :ratio="16 / 9">
                  <div class="relative w-full h-full">
                    <NuxtImg
                      v-if="getcover_photoUrl(featuredVideo)"
                      :src="getcover_photoUrl(featuredVideo)"
                      :alt="featuredVideo.title"
                      class="object-cover w-full h-full rounded-t-lg md:rounded-l-lg md:rounded-t-none"
                    />
                    <div
                      v-else
                      class="flex items-center justify-center w-full h-full bg-red-500 rounded-t-lg md:rounded-l-lg md:rounded-t-none"
                    >
                      <Icon name="uil:video" class="text-white w-16 h-16" />
                    </div>
                  </div>
                </AspectRatio>
              </div>
              <div class="p-6 flex flex-col justify-between">
                <div>
                  <h4 class="text-xl font-semibold line-clamp-2 mb-2">{{ featuredVideo.title }}</h4>
                  <p class="text-gray-500 mb-4">{{ featuredVideo.authorHandle }}</p>
                  <p class="text-sm text-gray-600 line-clamp-3">
                    {{ featuredVideo.description || 'No description available' }}
                  </p>
                </div>
                <Button size="lg" class="w-full" @click.stop="playVideo(featuredVideo)">
                  <Icon name="heroicons:play" class="w-5 h-5 mr-2" />
                  Play Video
                </Button>
              </div>
            </div>
          </Card>
        </AppLink>
        <Separator class="my-4" />
      </div>

      <div class="mb-6">
        <h3 class="text-xl font-semibold mb-4">Video Wall</h3>
        <div v-if="bubblesStore.loading" class="text-center py-8">
          <p class="text-gray-600">Loading videos...</p>
        </div>
        <div v-else-if="bubblesStore.error" class="text-center py-8 text-red-500">
          <p>{{ bubblesStore.error }}</p>
        </div>
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <AppLink v-for="video in filteredVideos" :key="video.uuid" :to="`/bubble/${video.uuid}`">
            <Card class="overflow-hidden cursor-pointer flex flex-col justify-between">
              <div>
                <CardContent class="p-0">
                  <AspectRatio :ratio="16 / 9">
                    <div class="relative w-full h-full">
                      <NuxtImg
                        v-if="getcover_photoUrl(video)"
                        :src="getcover_photoUrl(video)"
                        :alt="video.title"
                        class="object-cover w-full h-full transition-transform duration-300 hover:scale-110"
                      />
                      <div v-else class="flex items-center justify-center w-full h-full bg-red-500">
                        <Icon name="uil:video" class="text-white w-12 h-12" />
                      </div>
                    </div>
                  </AspectRatio>
                </CardContent>
                <CardHeader>
                  <CardTitle class="text-lg">{{ video.title }}</CardTitle>
                  <CardDescription>{{ video.authorHandle }}</CardDescription>
                </CardHeader>
              </div>
              <CardFooter class="flex justify-center mt-auto">
                <Button variant="default" size="sm" class="w-full" @click.stop="playVideo(video)"
                  >Play</Button
                >
              </CardFooter>
            </Card>
          </AppLink>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="canLoadMore" class="mt-6 text-center">
        <Button :disabled="isLoading" variant="default" class="hover:opacity-90" @click="loadMore">
          Load More Videos
        </Button>
      </div>
    </div>

    <Dialog v-model:open="isVideoModalOpen">
      <DialogContent class="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{{ selectedVideo?.title }}</DialogTitle>
          <DialogDescription>{{ selectedVideo?.authorHandle }}</DialogDescription>
        </DialogHeader>
        <div class="aspect-video">
          <iframe
            v-if="selectedVideo"
            :src="getEmbedUrl(selectedVideo.url)"
            class="w-full h-full"
            frameborder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowfullscreen
          />
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, h } from 'vue'
import { useBubblesStore } from '~/stores/bubbles'
import { useUserShareStore } from '~/stores/userShare'
import type { YouTubeBubble } from '~/stores/interfaces'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import FilterInput from '~/components/bubbles/view/helpers/FilterInput.vue'
import { useRouter } from 'vue-router'
import { useRuntimeConfig } from '#app'
import { useNavigationStore } from '@/stores/navigationStore'
import AppLink from '@/components/ui/link/AppLink.vue'
import { useToast } from '@/components/ui/toast'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import { useClipboard } from '@vueuse/core'

const router = useRouter()
const bubblesStore = useBubblesStore()
const navigationStore = useNavigationStore()
const userShareStore = useUserShareStore()
const { toast } = useToast()
const { copy, copied, isSupported } = useClipboard()
const searchQuery = ref('')
const isVideoModalOpen = ref(false)
const selectedVideo = ref<YouTubeBubble | null>(null)
const {
  public: { dataStore },
} = useRuntimeConfig()

const VIDEOS_PER_PAGE = 12
const page = ref(1)
const isLoading = ref(false)
const error = ref<string | null>(null)
const isSharingSet = ref(false)

const YouTubeBubbles = computed(
  () => (bubblesStore.bubblesByType['youtube'] || []) as YouTubeBubble[]
)

const filteredVideos = computed(() => {
  const filtered = YouTubeBubbles.value
    .filter((video) => video.title.toLowerCase().includes(searchQuery.value.toLowerCase()))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, page.value * VIDEOS_PER_PAGE)

  navigationStore.setCurrentType('youtube')
  navigationStore.setCurrentTypeItems(filtered.map((video) => video.uuid))

  return filtered
})

const canLoadMore = computed(() => {
  const totalFilteredCount = YouTubeBubbles.value.filter((video) =>
    video.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  ).length
  return totalFilteredCount > page.value * VIDEOS_PER_PAGE
})

const featuredVideo = ref<YouTubeBubble | null>(null)

function getNewFeaturedVideo() {
  if (YouTubeBubbles.value.length > 0) {
    const randomIndex = Math.floor(Math.random() * YouTubeBubbles.value.length)
    const randomVideo = YouTubeBubbles.value[randomIndex]
    if (randomVideo) {
      featuredVideo.value = randomVideo
    } else {
      featuredVideo.value = null
    }
  } else {
    featuredVideo.value = null
  }
}

function playVideo(video: YouTubeBubble) {
  selectedVideo.value = video
  isVideoModalOpen.value = true
}

function getEmbedUrl(url: string) {
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.split('v=')[1] || url.split('/').pop()
    return `https://www.youtube.com/embed/${videoId}`
  }
  return url
}

function navigateToAddVideo() {
  router.push('/dashboard/add')
}

function getcover_photoUrl(video: YouTubeBubble) {
  if (video.offlineMediaResources?.cover_photo) {
    return `${dataStore}${video.offlineMediaResources.cover_photo}`
  }
  if (video.coverPhoto) {
    return video.coverPhoto
  }
  return null
}

onMounted(async () => {
  // Clear search-related store
  searchQuery.value = ''

  if (!bubblesStore.bubbleTypes['youtube']) {
    await bubblesStore.fetchBubbleTypes()
  }

  if (
    !bubblesStore.bubblesByType['youtube'] ||
    bubblesStore.bubblesByType['youtube'].length < VIDEOS_PER_PAGE
  ) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('youtube')
    } catch (err) {
      error.value = 'Failed to load videos. Please try again later.'
      logError('Failed to fetch videos:', err)
    } finally {
      isLoading.value = false
    }
  }
  getNewFeaturedVideo()
})

async function loadMore() {
  page.value++
  if (page.value * VIDEOS_PER_PAGE >= YouTubeBubbles.value.length) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('youtube')
    } catch (err) {
      error.value = 'Failed to load more videos. Please try again later.'
      logError('Failed to fetch videos:', err)
    } finally {
      isLoading.value = false
    }
  }
}

const handleShareSet = async () => {
  isSharingSet.value = true
  try {
    const shareData = {
      shareType: 'set' as const,
      targetBubbleType: 'youtube',
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: 'Your set of YouTube videos can be shared via this link.',
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
      if (isSupported.value) {
        await copy(result.url)
        if (copied.value) {
          toast({
            title: 'Link Copied!',
            description: 'The share link has been copied to your clipboard.',
            duration: 3000,
          })
        } else {
          toast({
            title: 'Copy Failed',
            description:
              'Could not automatically copy the link. Please use the button in the previous message.',
            variant: 'destructive',
            duration: 5000,
          })
        }
      }
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link for set.',
          variant: 'destructive',
        })
      }
    }
  } catch (error) {
    logError('Error creating YouTube set share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing the set.',
      variant: 'destructive',
    })
  } finally {
    isSharingSet.value = false
  }
}
</script>
